import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import CategoryGrid from './components/CategoryGrid';
import Features from './components/Features';
import AboutTrust from './components/AboutTrust';
import Testimonials from './components/Testimonials';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import { blogArticles } from '@/mocks/blog-articles';

function BlogArticlesPreview() {
  const { t } = useTranslation();

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="w-full px-6 md:px-10">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full border border-secondary-300 text-xs font-medium text-foreground-600 mb-6">
            Resource Library
          </span>
          <h2 className="text-3xl md:text-[40px] font-bold text-foreground-950">
            {t('blog_preview.title', 'ItaobuySpreadsheet Guides & Resources')}
          </h2>
          <p className="mt-4 text-foreground-600 text-base max-w-xl mx-auto">
            {t('blog_preview.subtitle')}
          </p>
        </div>

        {/* Article Cards Grid - 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {blogArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="group block rounded-2xl bg-background-50 border border-secondary-200 overflow-hidden hover:border-secondary-300 transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="h-48 overflow-hidden bg-secondary-100">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              {/* Content */}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-medium whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-500">{article.date}</span>
                </div>
                <h3 className="text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                  {article.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {article.metaDescription}
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors duration-200">
                    Read Article →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View all link */}
        <div className="text-center mt-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-foreground-200 text-sm font-medium text-foreground-700 hover:bg-foreground-50 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-book-open-line"></i>
            View All Shopping Guides →
          </Link>
        </div>
      </div>
    </section>
  );
}

function ValueProposition() {
  const { t } = useTranslation();

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="w-full px-6 md:px-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left */}
          <div className="w-full lg:w-[45%]">
            <span className="inline-block px-4 py-1.5 rounded-full border border-secondary-300 text-xs font-medium text-foreground-600 mb-6">
              {t('value.label', 'Why Choose Us')}
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-foreground-950 leading-tight">
              {t('value.title')}
            </h2>
            <p className="mt-4 text-foreground-600 text-base leading-relaxed">
              {t('value.subtitle')}
            </p>
          </div>

          {/* Right - Benefits List */}
          <div className="w-full lg:w-[50%]">
            <div className="space-y-4">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-secondary-50">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-primary-600 text-sm"></i>
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    {t(`value.benefits.${i}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuy.boats';
  const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg';

  return (
    <div className="min-h-screen bg-background-50">
      {/* Schema.org Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Itaobuy Spreadsheet",
          "url": siteUrl,
          "logo": logoUrl,
          "description": "ItaobuySpreadsheet is a powerful spreadsheet-based product sourcing system for e-commerce sellers, dropshippers, and import businesses.",
          "sameAs": []
        })}
      </script>
      <Navbar />
      <main>
        <Hero />
        <CategoryGrid />
        <ValueProposition />
        <Features />
        <AboutTrust />
        <Testimonials />
        <BlogArticlesPreview />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
}